import { useDispatch, useSelector } from "react-redux";
import { Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton } from "@mui/material";
import { IconAlertTriangle, IconX } from "@tabler/icons";
import { setShowModal } from "src/store/customizer/CustomizerSlice";
import ActionButton, { iconButtonSx } from "src/components/shared/ActionButton";

// The in-app confirm every page uses before deleting or undoing something
// (never window.confirm). Same API as before:
//
//   const alert = CommonAlert();
//   alert.show({
//     title: "Delete this country?",
//     message: "Its prices and documents go too. This can't be undone.",
//     type: "delete",
//     actions: [
//       { name: "Delete", type: "delete", callback: () => ... },
//       { name: "Cancel", type: "cancel" },
//     ],
//   });
//
// It is a real dialog now: focus moves into it and back, Escape and the close
// button cancel, and screen readers announce the title and message.

export const CommonAlert = () => {
  const dispatch = useDispatch();

  const show = ({ title, message, actions = [], component, type }) => {
    dispatch(
      setShowModal({
        show: true,
        title,
        message,
        action: actions,
        type,
        component: type === "delete" ? null : component,
      }),
    );
  };

  const hide = () => {
    dispatch(setShowModal({ show: false, title: "", message: "", action: [], component: "", type: undefined }));
  };

  const handleActionPress = (action) => {
    const { callback } = action;
    if (callback) callback();
    hide();
  };

  return { show, hide, handleActionPress };
};

// Older call sites pass "Are you sure?" / "Yes" / "No" and a message with an
// exclamation mark. Say what will happen instead, in the standard's words,
// without touching every page.
const isCancel = (action) => action?.type === "cancel" || /^(no|cancel|close)$/i.test(action?.name || "");

function buttonLabel(action, type) {
  const name = String(action?.name ?? "").trim();
  if (isCancel(action)) return /^no$/i.test(name) ? "Cancel" : name || "Cancel";
  if (!/^(yes|ok|confirm|sure)$/i.test(name)) return name;
  if (type === "delete" || action?.type === "delete") return "Delete";
  if (action?.type && action.type !== "default") return action.type.charAt(0).toUpperCase() + action.type.slice(1);
  return "Continue";
}

function titleText(title, type) {
  const text = typeof title === "string" ? title.trim() : title;
  if (typeof text !== "string") return text;
  const named = text.match(/^are you sure (?:you want )?to delete\s*(.*?)\s*\?$/i);
  if (named) return named[1] ? `Delete ${named[1]}?` : "Delete this item?";
  if (/^are you sure\s*\??$/i.test(text) && type === "delete") return "Delete what you selected?";
  if (/^are you sure\s*\??$/i.test(text)) return "Do you want to continue?";
  return text;
}

function messageText(message, type) {
  if (typeof message !== "string") return message;
  if (/you won'?t be able to revert this!?/i.test(message)) {
    return type === "delete" ? "This can't be undone." : "Please check before you continue.";
  }
  return message.replace(/!+$/, ".");
}

const CommonAlertComponent = () => {
  const alert = CommonAlert();
  const modal = useSelector((state) => state.customizer.Modal);
  const actions = (modal?.action || []).filter(Boolean);
  const type = modal?.type || (actions.some((a) => a?.type === "delete") ? "delete" : undefined);
  const destructive = type === "delete";
  // Cancel first (left), the commit last (right), as on Cases.
  const ordered = [...actions.filter(isCancel), ...actions.filter((a) => !isCancel(a))];
  if (!ordered.some(isCancel)) ordered.unshift({ name: "Cancel", type: "cancel" });

  return (
    <Dialog
      open={!!modal?.show}
      onClose={() => alert.hide()}
      maxWidth="xs"
      fullWidth
      // The role goes on the paper (MUI's role="dialog" element), next to
      // the name and description; on the Dialog it landed on the backdrop root.
      PaperProps={{
        role: "alertdialog",
        "aria-labelledby": "common-alert-title",
        "aria-describedby": modal?.message ? "common-alert-message" : undefined,
      }}
    >
      <DialogTitle id="common-alert-title" sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, pr: 7 }}>
        {destructive && (
          <Box
            aria-hidden
            sx={{
              flexShrink: 0,
              width: 36,
              height: 36,
              borderRadius: 999,
              display: "grid",
              placeItems: "center",
              bgcolor: "secondary.light",
              color: (t) => (t.palette.mode === "dark" ? t.palette.secondary.main : t.palette.secondary.dark),
              mt: -0.25,
            }}
          >
            <IconAlertTriangle size={20} />
          </Box>
        )}
        <Box component="span" sx={{ pt: destructive ? 0.5 : 0 }}>
          {titleText(modal?.title, type)}
        </Box>
      </DialogTitle>
      {/* Outside the title, so the dialog's name is the title alone, not
          "… Close" (CMS review round 2, karim-11). */}
      <IconButton aria-label="Close" onClick={() => alert.hide()} sx={[iconButtonSx, { position: "absolute", right: 12, top: 12 }]}>
        <IconX size={20} />
      </IconButton>
      {(modal?.message || modal?.component) && (
        <DialogContent>
          {modal?.component ? <Box mb={1}>{modal.component}</Box> : null}
          {modal?.message ? (
            <DialogContentText id="common-alert-message" sx={{ color: "text.secondary" }}>
              {messageText(modal.message, type)}
            </DialogContentText>
          ) : null}
        </DialogContent>
      )}
      <DialogActions sx={{ flexWrap: "wrap" }}>
        {ordered.map((action, index) => {
          const cancel = isCancel(action);
          return (
            <ActionButton
              key={`${action?.name}-${index}`}
              size="medium"
              variant={cancel ? "text" : destructive || action?.type === "delete" ? "outlined" : "contained"}
              tone={!cancel && (destructive || action?.type === "delete") ? "destructive" : undefined}
              // Every confirm starts on the safe choice.
              autoFocus={cancel}
              onClick={() => alert.handleActionPress(action)}
            >
              {buttonLabel(action, type)}
            </ActionButton>
          );
        })}
      </DialogActions>
    </Dialog>
  );
};

export default CommonAlertComponent;
